// #region angular imports
import { AfterContentChecked, Component, computed, ElementRef, HostListener, inject, Input, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { 
	faAngleDown
	, faArrowLeft
	, faArrowRight
	, faUser
	, faGears
    , faGear
	, faQuestionCircle
	, faStaffSnake
	, faBarcodeRead
	, faSwap
	, faSun
	, faMoon
	, faShelves
	, faHouse
    , faComputerClassic
} from '@fortawesome/pro-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
// #endregion

// #region S2 imports
import { SelectItem } from '../../models/select-item';
import { GlobalService } from '../../service/global.service';
import { pcgSettings } from '../../generated/pcg-settings';
import { SecurityService } from '../../../core/security/security.service';
import { deleteAllCookies } from '../../../core/security/delete-all-cookies';
import { NavigationService } from '../navigation.service';
import { UserJsVm } from '../../generated/Admin/Models/User/UserJsVm';
import { GlobalVariablesService } from '../../../services/global-variables.service';
import { SwapClientVm } from '../../generated/Admin/Models/SwapClientVm';
import { PermissionProfileEnum } from '../../../core/enums/generated/PermissionProfileEnum';
import { PermissionRowIds } from '../../../core/security/permission-profiles';
import { CheckNetworkService } from '../../../../local-db/services/check-network.service';
import { AlertModalService } from '../../form-elements/components/alert-modal/alert-modal.service';
import { UserTypeEnum } from '../../../core/enums/generated/UserTypeEnum'
import { getMainNavRoutes } from '../../../core/main-nav-routes';
import { NotificationService } from '../../../services/notification.service';
import { NavInventorySiteVm } from '../../generated/Admin/Models/User/NavInventorySiteVm';
import { ThemeService } from '../../../services/theme.service';
import { UserThemeEnum } from '../../../core/enums/generated/UserThemeEnum';
import { resolveWitnessLanding } from '../../../core/security/witness-custody-landing';
import { WitnessCustodyAccessVm } from '../../generated/Inventory/Models/ShiftChange/WitnessCustodyAccessVm';
// #endregion

@UntilDestroy()
@Component({
    selector: 'pcg-mat-side-nav'
    , templateUrl: './mat-side-nav.component.html'
    , styleUrls: ['./mat-side-nav.component.scss']
    , standalone: false
})
export class MatSideNavComponent implements OnInit, AfterContentChecked {

    // #region injections
    sec = inject(SecurityService);
    modalService = inject(NgbModal);
    httpClient = inject(HttpClient);
    alertModalService = inject(AlertModalService);
    ns = inject(NotificationService);
    themeService = inject(ThemeService);
    navService = inject(NavigationService);
    public router = inject(Router);
    public globalService = inject(GlobalService);
    public gvs = inject(GlobalVariablesService);    
    // #endregion

    // #region ViewChild / Input
    @ViewChild('modalChangeClient', { static: true }) modalChangeClient!: TemplateRef<any>;
    @ViewChild('agencySearch') agencySearch?: ElementRef<HTMLInputElement>;

    @Input() user!: UserJsVm;
    // #endregion

    // #region nav items
	navContentOffscreen = signal<boolean>(false);
	// Setting the scroll height used to keep the nested table scrollable with headers shown.
	observer = new IntersectionObserver(entries => { this.navContentOffscreen.set(!entries[0]?.isIntersecting); });
	// Used to display a loading spinner on the navigation link when clicked, notifying the user that the page is loading.
	navLinkLoading = signal<[string, number]>([null!, null!]);
    // #endregion
    
    // #region models
    clientItems = signal<SelectItem[]>([]);

    // S2N-3242: the agency switcher filters in place now. The list is rendered inline in the
    // dialog rather than in a floating overlay panel, because an overlay panel would not take
    // touch scrolling on iOS, so the whole list was unreachable on a phone.
    agencyFilter = signal<string>('');

    // Collapsed until asked for, so the dialog opens showing the current agency rather than a
    // wall of 180 of them.
    agencyListOpen = signal<boolean>(false);

    readonly filteredClientItems = computed(() => {
        const search = this.agencyFilter().trim().toLowerCase();
        const items = this.clientItems();
        if (!search) { return items; }
        return items.filter(o => o.text?.toLowerCase().includes(search));
    });

	inventoriesDEA: any[] = [];
    formGroup = SwapClientVm.Form;
	model = SwapClientVm.GetModel(this.formGroup);
    // #endregion

    // #region strings
    mode = pcgSettings.mode;
    clientName: string = "";
	hoverText: string = "";
	fileLogoUrl = "../assets/images/logos/logo_small.png";
	navName = "";
    themeOption = ""; 
    themes = [ "Light", "Dark", "System" ];
    currentUrl: string = "";
    // #endregion

    // #region numbers
	clientId!: number;
    currentClientId = 0;
	navigationOffset = 0;
    // #endregion

    // #region booleans
	showSwapClient = false;
	isDarkMode = false;
    isSysAdmin = false;
    isAdmin = false;
    isManager = false;
    isProvider = false;
    isWitness = false;
    /**
     * S2N-1420: whether this profile has operational access at all (CoreFeatures). Hand-written
     * nav links must test THIS rather than "not an Admin/Manager/Provider" — a restricted profile
     * satisfies every one of those negative tests and so was shown links it can never open.
     */
    isOperational = false;
    /**
     * S2N-1420: whether this profile may READ the products and medication-use records of the
     * inventories it is associated to. True for every profile — an operational one reaches those
     * screens through CoreFeatures anyway — so in practice it is what keeps the two read-only
     * links visible for a restricted profile (Witness) while the operational ones stay hidden.
     */
    canViewAssociatedInventories = false;
    /**
     * S2N-3076: whether this profile may READ everything in the active agency (AgencyRead). Held by
     * View Only, and by Admin and System Admin too, so a hand-written LIST/READ link tests
     * `isOperational || canReadAgency`; a create/"new" action keeps testing `isOperational` alone —
     * the server refuses the write for View Only and the link would only lead to a 403.
     */
    canReadAgency = false;
    /**
     * S2N-1420: a profile that may READ associated-inventory records but has no operational
     * access — the Witness. The server honors its read grant only for such a caller
     * (PermissionFilter), so a Manager-only link (reconciliations, return logs, DEA forms) may be
     * shown to a Witness without also appearing for a Provider.
     */
    isRestrictedReader = false;
    /**
     * S2N-3075: whether this Witness has somewhere to go Home TO.
     *
     * Home is hidden for a base Witness on purpose — DashboardComponent redirects them straight
     * to their account-security page, so the link would be a house icon that lands on a password
     * form. A possession-enabled Witness gets the real possession dashboard there, and so gets
     * the link.
     *
     * The three facts this depends on — the agency flag as it is right now, whether they hold an
     * inventory, and whether they are inside the disablement grace — are none of them on the
     * cached user object, so this asks the server (the same GetWitnessCustodyAccess the dashboard
     * asks) and reads the answer through the same resolver. It deliberately does NOT read
     * user.useWitnessPossession: that cached copy misses the grace, which would take Home
     * away from a Witness at the exact moment they are mid-handoff and need it most.
     *
     * Fails closed, and false for every non-Witness profile — an operational profile's Home is
     * decided by isOperational, as before.
     */
    witnessHasCustody = false;
    isApiUser = false;
    isTransferLockedForTechs!: boolean;
    isProduction = false;
    // #endregion

	// Font awesome
    faIconName = { 
		faAngleDown
        , faStaffSnake
        , faGear
		, faGears
		, faUser
		, faQuestionCircle
		, faBarcodeRead
		, faArrowLeft
		, faArrowRight
		, faSwap
		, faSun
		, faMoon
		, faShelves
		, faHouse
        , faComputerClassic
	};

    xDown = null;
	yDown = null;

	@HostListener('window:resize') onResize() { this.fixNavOffset(); }

    ngOnInit() {
        this.currentUrl = globalThis.location.href;
        if (
            this.currentUrl.startsWith('https://app.schedule2.it') 
            || this.currentUrl.startsWith('https://app1.schedule2.it')
            || this.currentUrl.startsWith('https://app2.schedule2.it')
            || this.currentUrl.startsWith('https://app3.schedule2.it')
            || this.currentUrl.startsWith('https://app4.schedule2.it')
            || this.mode.toLowerCase() === 'production'
        ) { this.isProduction = true; } 
        else { this.isProduction = false; }
        this.navService.navRoutes.set(this.sec.getSecureNavItems(getMainNavRoutes()));
		this.navService.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
		// S2N-3075: re-asked on every security change, not just at init — swapping agencies is
		// exactly the case the AC calls out, and possession in one agency must not show Home in
		// another. ngAfterContentChecked is where the other profile flags are recomputed, but it
		// runs on every change-detection pass and so is no place to start a request.
		this.refreshWitnessCustody();
		this.sec.security$
			.pipe(untilDestroyed(this))
			.subscribe(() => this.refreshWitnessCustody());
		this.navService.navRoutes$
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.fixNavOffset();
				this.navLinkLoading.set([null!, null!]);
			}
		);

	    this.gvs.currentClientName.set(this.user?.clientName!);
		this.clientName = this.gvs.currentClientName();
		this.gvs.currentClientId.set(this.user?.currentClientId);		
		this.currentClientId = this.gvs.currentClientId();

        if (
			this.user?.permissionId === PermissionRowIds.sysAdmin
			|| this.user?.clientCount > 1
		) { this.getClients() }; 
		this.getInventories();
		this.getInventoriesDEA();
		this.clientId = this.user?.clientId!;

		if (
			this.user?.clientLogoFilename !== "" 
			&& this.user?.clientLogoFilename !== undefined 
			&& this.user?.clientLogoFilename !== null
		) { this.fileLogoUrl = this.user?.clientLogoFilePath!; }

        //event litseners for swiping away side nav in mobile
		document.addEventListener('touchstart', (evt) => this.handleTouchStart(evt), false);        
		document.addEventListener('touchmove', (evt) => this.handleTouchMove(evt), false);

		// Checking if there is any nave content off-screen.
		const navAnchor = document.getElementById("navAnchor");
		this.observer?.observe(navAnchor!);
        this.setThemeOption();
    }

    // #region theme
    setThemeOption() {
        let userTheme: UserThemeEnum = this.user?.userTheme;
        if (userTheme != null) {
            switch (userTheme) {
                case UserThemeEnum.Light: this.themeOption = "Light"; break;
                case UserThemeEnum.Dark: this.themeOption = "Dark"; break;
                case UserThemeEnum.System: this.themeOption = "System"; break;
                default: this.themeOption = "System"; break;
            }
        }
    }

    toggleTheme(event: any) {
        const prevOption = this.themeOption;
        if (event.value === prevOption) { return; }
        this.themeOption = event.value;
        let theme: UserThemeEnum;
        switch (event.value) {
            case "System": theme = UserThemeEnum.System; break;
            case "Light": theme = UserThemeEnum.Light; break;
            case "Dark": theme = UserThemeEnum.Dark; break;
            default: theme = UserThemeEnum.System; break;
        }
        this.httpClient.post<any>(`api/Admin/Users/SaveUserTheme`
            + `?theme=${theme}` 
            , {}
        ).subscribe((sm: any) => { 
            if (sm.isSuccessful) { 
                this.themeService.setTheme(theme);
                this.ns.showSuccess(sm.message, { timeOut: 2000});
            } else { this.ns.showError(sm.message, { timeOut: 5000}); }
        });
    }
    // #endregion

	fixNavOffset() {
		const mainNav = document.getElementById("pcgMainNavigation");
		setTimeout(() => {
			if (getComputedStyle(mainNav!, null).position === "fixed") { this.navigationOffset = mainNav?.getBoundingClientRect()?.bottom!; } 
			else { this.navigationOffset = 0; }

			const styles = `pcg-side-nav { top: ${this.navigationOffset - 1}px }` 
				+ `.nav-padding-placeholder { padding-top: ${this.navigationOffset}px; }` 
				+ `.conversations-sidenav { top: ${this.navigationOffset}px }` 
				+ `.knowledge-base-doc-popout { top: ${this.navigationOffset}px }` 
				+ `.popout { top: ${this.navigationOffset}px }`;

			const tableStyles = `.pcg-table-fixed-header thead tr th, ` 
                + `.pcg-table-fixed-header thead tr { top: ${this.navigationOffset - 1}px }`;

			this.setDynamicStyle("navPaddingOffset", styles);
			this.setDynamicStyle("navTableOffset", tableStyles);
		}, 100);
	}

	setDynamicStyle(
        name: string
        , styles: string
    ) {
		let dynamicStyle = document.getElementById(name);
		if (dynamicStyle) { dynamicStyle.remove(); }
		dynamicStyle = document.createElement("style");
		dynamicStyle.id = name;
		dynamicStyle.innerHTML = styles;
		document.head.appendChild(dynamicStyle);
	}

	// Navigate/redirect to new page
	redirect(path: string) { this.router.navigateByUrl(path); }

	/**
	 * S2N-3075: ask the server whether this Witness has a custody experience, and so a Home.
	 * Only a Witness asks — no other profile's navigation depends on the answer, and an
	 * operational caller would get CanStart true and nothing else useful.
	 */
	private refreshWitnessCustody() {
		if (!this.sec.hasAccess(o => o.permissionProfile === PermissionProfileEnum.Witness)) {
			this.witnessHasCustody = false;
			return;
		}
		this.httpClient.get<WitnessCustodyAccessVm>('api/Inventory/ShiftChange/GetWitnessCustodyAccess')
			.pipe(untilDestroyed(this))
			.subscribe({
				next: (access: WitnessCustodyAccessVm) => {
					this.witnessHasCustody = resolveWitnessLanding(access).showCustodyExperience;
				}
				// An unreachable server is not a reason to offer a link whose destination would
				// then bounce them. Same fail-closed choice the dashboard makes.
				, error: () => { this.witnessHasCustody = false; }
			});
	}

    ngAfterContentChecked() {
        this.isSysAdmin = this.sec.hasAccess(o => o.permissionProfile == PermissionProfileEnum.SysAdmin);
        this.isAdmin = this.sec.hasAccess(o => o.permissionProfile == PermissionProfileEnum.Admin);
        this.isManager = this.sec.hasAccess(o => o.permissionProfile == PermissionProfileEnum.Manager);
        this.isProvider = this.sec.hasAccess(o => o.permissionProfile == PermissionProfileEnum.Provider);
        this.isWitness = this.sec.hasAccess(o => o.permissionProfile == PermissionProfileEnum.Witness);
        this.isOperational = this.sec.hasAccess(o => !!o.coreFeatures);
        this.canViewAssociatedInventories = this.sec.hasAccess(o => !!o.associatedInventoryView);
        this.canReadAgency = this.sec.hasAccess(o => !!o.agencyRead);
        this.isRestrictedReader = this.canViewAssociatedInventories && !this.isOperational;
		this.showSwapClient = this.user?.clientCount > 1
			&& (
				!this.user.isUsingSso
				|| this.user.isInSsoCascadeCorporate
			);
        this.isApiUser = this.user?.userType == UserTypeEnum.ApiOnly;
        this.isTransferLockedForTechs = this.user?.isTransferLockedForTechs;
    }

    openInNewTab(namedRoute: string) {
		let newRelativeUrl = this.router.createUrlTree([namedRoute]);
		let baseUrl = globalThis.location.href.replace(this.router.url, '');
		window.open(baseUrl + newRelativeUrl, '_blank');
		this.navService.isNavOpen.set(false);
	}

	toggleUserProfileSubNav() {
		this.gvs.userInvMenu().forEach(o => o.value = false);
		const isOpen = this.navService.userProfileExpanded();
		this.navService.collapseAllSubNavs();
		if (!isOpen) { this.navService.userProfileExpanded.set(true); }
	}

	toggleInventoriesSubNav() {
		this.gvs.userInvMenu().forEach(o => o.value = false);
		const isOpen = this.navService.inventoriesExpanded();
		this.navService.collapseAllSubNavs();
		if (!isOpen) { this.navService.inventoriesExpanded.set(true); }
	}

	toggleInventorySubNav(inventoryName: string) {
		this.gvs.userInvMenu().forEach(o => {
			if (o.text === inventoryName) { o.value = !o.value; } 
			else { o.value = false; }
		});
	}

	handleTouchStart(evt: any) {
		if (
			window.innerWidth < 993 
			&& this.navService.isNavOpen()
		) {
			let firstTouch = evt.touches || evt.originalEvent.touches;       
			this.xDown = firstTouch[0].clientX;                                      
			this.yDown = firstTouch[0].clientY; 
		}			                                   
	};                                                
																				
	handleTouchMove(evt: any) {
		if (
			this.navService.isMobile() 
			&& this.navService.isNavOpen()
		) {
			if (
				!this.xDown 
				|| !this.yDown
			) { return; }
	
			let xUp = evt.touches[0].clientX;                                    
			let yUp = evt.touches[0].clientY;
			let xDiff = this.xDown - xUp;
			let yDiff = this.yDown - yUp;
																				
			if (Math.abs(xDiff) > Math.abs(yDiff)) {/*most significant*/
                /* right swipe */ 
				if (xDiff > 8) { this.navService.isNavOpen.set(false); } 
                else { /* left swipe */ }                       
			} 
			/* reset values */
			this.xDown = null;
			this.yDown = null;  
		}
	}

	navLinkClicked(
		name: any 
		, level: any
		, openNewTab = false
		, path = ""
	) {
		if (openNewTab) { window.open(path); }
		else {
			if (this.navService.isMobile()) { this.navService.isNavOpen.set(false); }
			this.navLinkLoading.set([name, level]);
			setTimeout(() => { this.navLinkLoading.set([null!, null!]); }, 500);
		}
		
	}

	navLinkIsLoading(
		name: any 
		, level: any
	) {
		const [currentName, currentLevel] = this.navLinkLoading();
		return (currentName === name && currentLevel === level);
	}

	scrollToBottom() {
		const navAnchor = document.getElementById('navAnchor');
		if (navAnchor) { navAnchor.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' }); }
	}

	openKnowledgeBase() { window.open("https://support.schedule2.it/", "_blank"); }

	// #region Logout
	logout() {
		let isOnline = CheckNetworkService.checkNetworkStatus();
		if (isOnline) { this.executeLogout(); } 
		else {
			this.alertModalService.openAlert(
				"Are you sure you want to log out? You are offline and will not be able to log in until you are back online."
				, () => { this.executeLogout(); }
			);
		}
	}

	executeLogout() {
		this.gvs.isNavFixed.set(false);
		this.navService.collapseAllSubNavs();
		localStorage.removeItem("subNavPosition");
        this.themeService.setTheme(UserThemeEnum.Light);
		this.httpClient.post('api/Account/Logout', {}).subscribe((msg: any) => {
			if (msg.value != null) { globalThis.location.href = msg.value; }
			if (!msg.isSuccessful) { this.ns.showError(msg.message, {timeOut: 5000}); }
		});
		this.sec.setSecurity(null, null, null);
		sessionStorage.clear();
		deleteAllCookies();
		this.router.navigate(['/login']);
	}

	// #endregion  

	// #region Inventories
	getInventories() {
		//when using hide tech inventories feature
        let invs: NavInventorySiteVm[] = [];
        let inventorySubMenu: SelectItem[] = [];
		if (
			this.user?.useHideTechnicianInventories 
			&& this.user?.permissionId === PermissionRowIds.provider
		) {
			this.user?.inventorySites?.map(o => {
				if (this.user?.responsibleInventories?.includes(o.id)) {					
					invs.push(o);
                    inventorySubMenu.push({ text: o.name!, value: false });
				}
			});            
		} else {
            invs = this.user?.inventorySites!;
			this.user?.inventorySites?.map(o => { inventorySubMenu.push({ text: o.name!, value: false }); });
		}
        this.gvs.inventories.set(invs);
        this.gvs.userInvMenu.set(inventorySubMenu);
	}

	getInventoriesDEA() { this.inventoriesDEA = this.gvs.inventories()?.filter(inventory => inventory.inventoryType === 3); }
	// #endregion

	// #region Change Customer
	getClients() { 
		if (this.user?.permissionId === PermissionRowIds.sysAdmin) { 
			this.httpClient.get(`api/Select/Clients`).subscribe((items: any) => { this.clientItems.set(items); }); 
		} else if (this.user?.clientCount > 1) {
			this.httpClient.get(`api/Select/ClientsByUser?id=${this.user.id}`).subscribe((items: any) => { this.clientItems.set(items); });
		}
	}

	openChangeClientModal() {
        this.agencyFilter.set('');
        this.agencyListOpen.set(false);
        this.modalService.open(this.modalChangeClient, { beforeDismiss: () => { return true; } });
        this.model = SwapClientVm.GetModel(this.formGroup);
		this.formGroup?.controls?.clientId?.patchValue(this.user?.currentClientId);
    }

    // A plain method rather than a computed: currentClientId is an ordinary property, so a
    // computed would never see it change.
    selectedAgencyText(): string {
        return this.clientItems().find(o => o.value === this.currentClientId)?.text ?? 'Select an agency';
    }

    toggleAgencyList() {
        const opening = !this.agencyListOpen();
        if (opening) { this.agencyFilter.set(''); }
        this.agencyListOpen.set(opening);
        // The field only exists once the list has rendered, so focus it on the next tick.
        if (opening) { setTimeout(() => this.agencySearch?.nativeElement.focus()); }
    }

    // Picking one collapses the list again, so the dialog goes back to reading as the single
    // line it opened as, now naming the new agency.
    selectAgency(val: number) {
        this.changeClient(val);
        this.agencyListOpen.set(false);
    }

    changeClient(val: number) {
		this.currentClientId = val;
		this.model.clientId = this.currentClientId;
		this.formGroup?.controls?.clientId?.patchValue(this.currentClientId);
	}

    saveChangeClient(modal: { dismiss: () => void; }) {
		this.httpClient.post('api/SetClient', this.formGroup.getRawValue())
            .subscribe(async (sm: any) => {
                if (sm.isSuccessful) { 		
					if (sm.value !== null) {
						let newUserInfo: UserJsVm = sm.value?.user;	
						this.sec.setSecurity(sm.value?.token, newUserInfo, sm.value?.security);
						this.clientName = newUserInfo?.clientName!;
						this.gvs.currentClientName.set(newUserInfo?.clientName!);
						this.currentClientId = newUserInfo?.currentClientId;
						this.gvs.currentClientId.set(newUserInfo?.currentClientId!);

						this.gvs.setClientCustomNames(newUserInfo);
	
						// use window.location.href to force refresh on redirect
						if (newUserInfo.needToResetPin === true) {
							globalThis.location.href = document.getElementsByTagName('base')[0].href
								+ `admin/users/edit/security/${newUserInfo.id}?r=1`;
						} else {
							globalThis.location.href = document.getElementsByTagName('base')[0].href + "dashboard";
						}
						modal.dismiss();
					}
				} else { this.ns.showError(sm?.message, {timeOut: 5000}); }
            }
		);
	}
	// #endregion
}
